"use client";

import { useState, useEffect } from "react";

const COOKIE_NAME = "cookie_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Check if consent cookie exists
    const consentCookie = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${COOKIE_NAME}=`));
    
    // Show banner if no consent cookie is found
    if (!consentCookie) {
      setShow(true);
    }
  }, []);

  const setConsentCookie = (value: "essential" | "all") => {
    // Set the cookie
    document.cookie = [
      `${COOKIE_NAME}=${value}`,
      `path=/`,
      `max-age=${COOKIE_MAX_AGE}`,
      `SameSite=Lax`,
      `Secure`, 
    ].join("; ");

    // Dispatch custom event for real-time reaction
    try {
      const event = new CustomEvent('consentChanged', { 
        detail: { value },
        bubbles: true,  // Allow event to bubble up the DOM tree
        cancelable: true // Allow event to be canceled
      });
      window.dispatchEvent(event);
      console.log(`Cookie consent set to: ${value}`);
    } catch (error) {
      console.error('Error dispatching consent event:', error);
    }

    setShow(false);
  };

  const handleAcceptEssential = () => {
    setConsentCookie("essential");
    // No analytics scripts will be loaded
    console.log("Essential cookies accepted.");
  };

  const handleAcceptAll = () => {
    setConsentCookie("all");
    // Analytics scripts will be loaded
    console.log("All cookies accepted.");
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed bottom-6 right-6 max-w-sm bg-[#121212]/80 backdrop-blur-md text-gray-300 border border-gray-800 rounded-xl p-5 shadow-xl z-50"
    >
      <div className="text-sm mb-4">
        We use cookies to improve your experience. By using our site, you accept our
        <a
          className="text-blue-400 hover:text-blue-300 transition-colors ml-1"
          href="/privacy-policy"
        >
          Privacy Policy
        </a>
        . Please choose your cookie preference.
      </div>
      <div className="flex space-x-3 justify-end">
        <button
          onClick={handleAcceptEssential}
          className="px-4 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
          aria-label="Accept only essential cookies"
        >
          Accept Essential
        </button>
        <button
          onClick={handleAcceptAll}
          className="px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          aria-label="Accept all cookies including analytics"
        >
          Accept All
        </button>
      </div>
    </div>
  );
} 